import { GetLocationVarFromVariables, StringHelper, warn } from "@swivl-mono/helpers";
import { LocationVarKey } from "@swivl-mono/types";
import { type HTMLProps, type ReactNode, useMemo } from "react";

import { filterClasses } from "../../Helpers/FilterClasses";
import LocationsModel from "../../Models/Locations/Locations.Model";
import LocationVarsModel from "../../Models/Variables/LocationVars.Model";
import "./DataCard.scss";

type Outlets = "wrapper" | "content" | "header" | "footer";

interface UserCardProps extends Omit<HTMLProps<HTMLDivElement>, "size" | "className"> {
  classNames?: { [Key in Outlets]?: string };
  footer?: ReactNode;
  locationId?: number;
  pills?: ReactNode;
  size?: "compact" | "full";
  title?: string;
  isRounded?: boolean;
  variant?: "primary" | "secondary";
}

export function DataCardLocation({
  classNames = {},
  footer,
  isRounded = true,
  locationId,
  pills,
  size = "compact",
  title,
  variant = "primary",
  ...rest
}: UserCardProps) {
  const { orgVariables, groupVariables, locationVariables, isLoaded } = LocationVarsModel.useState((s) => s);
  const { locations } = LocationsModel.useState((s) => s);

  const variants = {
    primary: "bg-grey-100",
    secondary: "bg-white",
  };

  const internalClassNames = {
    wrapper: filterClasses("px-4 py-3 transition-colors duration-200 ease-in-out", variants[variant], isRounded && "rounded-md", classNames.wrapper || ""),
    content: filterClasses("DataCardLocationContent", classNames.content || ""),
    header: filterClasses("DataCardLocationHeader", classNames.header || ""),
    footer: filterClasses("DataCardLocationFooter", classNames.footer || ""),
  };

  const locationVars = useMemo(() => {
    if (!locationId) {
      // warn('Location not set')
      return;
    }
    if (!locations) {
      // warn('locations not loaded');
      return;
    }
    const location = locations.find((l) => l.id === locationId);

    if (!location) {
      warn("Locations not found");
      return;
    }

    const name = GetLocationVarFromVariables(
      isLoaded,
      orgVariables,
      groupVariables,
      locationVariables,
      LocationVarKey.Name,
      location.groupId,
      location.id,
    );
    const address = GetLocationVarFromVariables(
      isLoaded,
      orgVariables,
      groupVariables,
      locationVariables,
      LocationVarKey.Address,
      location.groupId,
      location.id,
    );
    const email = GetLocationVarFromVariables(
      isLoaded,
      orgVariables,
      groupVariables,
      locationVariables,
      LocationVarKey.Email,
      location.groupId,
      location.id,
    );
    const phone = GetLocationVarFromVariables(
      isLoaded,
      orgVariables,
      groupVariables,
      locationVariables,
      LocationVarKey.Phone,
      location.groupId,
      location.id,
    );
    const officeHours = GetLocationVarFromVariables(
      isLoaded,
      orgVariables,
      groupVariables,
      locationVariables,
      LocationVarKey.OfficeHours,
      location.groupId,
      location.id,
    );
    const accessHours = GetLocationVarFromVariables(
      isLoaded,
      orgVariables,
      groupVariables,
      locationVariables,
      LocationVarKey.AccessHours,
      location.groupId,
      location.id,
    );
    const avatarUrl = GetLocationVarFromVariables(
      isLoaded,
      orgVariables,
      groupVariables,
      locationVariables,
      LocationVarKey.AvatarURL,
      location.groupId,
      location.id,
    );

    return {
      name: name?.value,
      address: address?.value,
      email: email?.value,
      phone: phone?.value,
      officeHours: officeHours?.value,
      accessHours: accessHours?.value,
      avatarUrl: avatarUrl?.value,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, locations]);

  if (locationId === undefined) {
    // warn('User location not set')
    return;
  }

  return (
    <div className={internalClassNames.wrapper} {...rest}>
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-left flex items-center space-x-2 pr-3">
            {title && <h3 className="font-semibold font-sans text-black">{locationVars?.name}</h3>}
            <div className="flex gap-2">{pills}</div>
          </div>
          <div className="text-left text-black text-sm">
            <p>{locationVars?.address}</p>
            <p>{locationVars?.email}</p>
            <p>{locationVars?.phone}</p>
          </div>
        </div>
        {locationVars?.avatarUrl && (
          <img className="size-20 object-cover rounded-sm" src={locationVars?.avatarUrl} alt={locationVars?.name + " logo"} />
        )}
      </div>
      {size === "full" && (
        <div className="flex mt-2.5 text-sm space-x-1.5">
          <div className="w-1/2 flex flex-col">
            <p className="font-semibold">Office Hours</p>
            <p className="whitespace-pre-line">{StringHelper.replaceNewLineWithLineBreak(locationVars?.officeHours)}</p>
          </div>
          <div className="w-1/2 flex flex-col">
            <p className="font-semibold">Access Hours</p>
            <p className="whitespace-pre-wrap">{StringHelper.replaceNewLineWithLineBreak(locationVars?.accessHours)}</p>
          </div>
        </div>
      )}
      {footer && <div className="mt-2.5 text-sm mb-1">{footer}</div>}
    </div>
  );
}
